import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type WorkspaceRole = 'workspace_owner' | 'workspace_member' | 'workspace_viewer' | 'delivery_owner' | 'stakeholder';

type WorkspaceMember = {
  id: string;
  userId: string;
  user?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
};

interface WorkspaceMemberCache {
  members: WorkspaceMember[];
  timestamp: number;
}

interface WorkspaceState {
  activeWorkspaceId: string | null;
  /**
   * Optional UI hint for useEffectiveRole. Not hydrated in production today —
   * live write/read gates must use `useWorkspaceRole` (FIX-USE-TEMPLATE-BUTTON-DISABLED-1).
   * Cleared on workspace clear; no setter (former setWorkspaceRole was never called).
   */
  workspaceRole: WorkspaceRole | null;
  workspaceReady: boolean; // Patch 1: true only when activeWorkspaceId exists

  // PROMPT 4: Workspace hydration status
  isHydrating: boolean;
  lastHydratedAt: number | null;
  hydratedWorkspaceIds: Set<string>;

  // PHASE 7 MODULE 7.1 FIX: Member cache per workspace
  memberCache: Record<string, WorkspaceMemberCache>;

  /** Bumped after workspace create so sidebar refetches `listWorkspaces`. Not persisted. */
  workspacesDirectoryNonce: number;
  bumpWorkspacesDirectory: () => void;

  /**
   * Until `listWorkspaces` returns the new row, merge this into the sidebar list so
   * favorites-only filtering does not hide the active workspace (empty rail bug).
   */
  sidebarWorkspacePlaceholder: { id: string; name: string } | null;
  setSidebarWorkspacePlaceholder: (value: { id: string; name: string } | null) => void;

  setActiveWorkspace: (id: string | null) => void;
  clearActiveWorkspace: () => void;

  // PROMPT 4: Hydration methods
  setHydrating: (isHydrating: boolean) => void;
  markWorkspaceHydrated: (workspaceId: string) => void;
  clearHydration: () => void;

  // PHASE 7 MODULE 7.1 FIX: Member cache methods
  setWorkspaceMembers: (workspaceId: string, members: WorkspaceMember[]) => void;
  getWorkspaceMembers: (workspaceId: string) => WorkspaceMember[] | null;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      activeWorkspaceId: null,
      workspaceRole: null,
      workspaceReady: false, // Patch 1: computed from activeWorkspaceId

      // PROMPT 4: Hydration state
      isHydrating: false,
      lastHydratedAt: null,
      hydratedWorkspaceIds: new Set<string>(),

      // PHASE 7 MODULE 7.1 FIX: Member cache
      memberCache: {},

      workspacesDirectoryNonce: 0,
      bumpWorkspacesDirectory: () =>
        set((s) => ({ workspacesDirectoryNonce: s.workspacesDirectoryNonce + 1 })),

      sidebarWorkspacePlaceholder: null,
      setSidebarWorkspacePlaceholder: (value) => set({ sidebarWorkspacePlaceholder: value }),

      setActiveWorkspace: (id) => set({
        activeWorkspaceId: id,
        workspaceReady: !!id, // Patch 1: workspaceReady true only when id exists
      }),
      clearActiveWorkspace: () => set({
        activeWorkspaceId: null,
        workspaceReady: false,
        workspaceRole: null,
      }),

      // PROMPT 4: Hydration methods
      setHydrating: (isHydrating: boolean) => set({ isHydrating }),
      markWorkspaceHydrated: (workspaceId: string) => {
        const current = get().hydratedWorkspaceIds;
        const updated = new Set(current);
        updated.add(workspaceId);
        set({
          hydratedWorkspaceIds: updated,
          lastHydratedAt: Date.now(),
          isHydrating: false,
        });
      },
      clearHydration: () => set({
        isHydrating: false,
        hydratedWorkspaceIds: new Set(),
        lastHydratedAt: null,
      }),

      // PHASE 7 MODULE 7.1 FIX: Member cache methods (5 minute TTL)
      setWorkspaceMembers: (workspaceId, members) => {
        const cache: WorkspaceMemberCache = {
          members,
          timestamp: Date.now(),
        };
        set((state) => ({
          memberCache: {
            ...state.memberCache,
            [workspaceId]: cache,
          },
        }));
      },
      getWorkspaceMembers: (workspaceId) => {
        const state = get();
        const cache = state.memberCache[workspaceId];
        const TTL = 5 * 60 * 1000; // 5 minutes
        if (cache && Date.now() - cache.timestamp < TTL) {
          return cache.members;
        }
        return null;
      },
    }),
    {
      name: 'workspace-storage',
      partialize: (state) => ({
        activeWorkspaceId: state.activeWorkspaceId,
        // Don't persist role or hydration state - fetch fresh on load
      }),
      // `workspaceReady` is not persisted, so derive it from the rehydrated id.
      // Without this a reload (or a typed /projects) left an id with ready=false.
      merge: (persisted, current) => {
        const stored = (persisted as Partial<WorkspaceState> | undefined)?.activeWorkspaceId;
        const activeWorkspaceId = typeof stored === 'string' && stored ? stored : null;
        return {
          ...current,
          ...(persisted as Partial<WorkspaceState>),
          activeWorkspaceId,
          workspaceReady: !!activeWorkspaceId,
        };
      },
    }
  )
);
